import React from 'react';
import { cn } from '../../lib/utils';

export interface FormSectionProps {
  /** Section heading (rendered as an h3). Omit for an untitled card. Accepts
   *  nodes for a required marker (`<span className="text-destructive">*</span>`). */
  title?: React.ReactNode;
  /** Sub-text under the heading. Accepts nodes for inline links/markup. */
  description?: React.ReactNode;
  /** Right-aligned controls in the section header (e.g. a per-section toggle). */
  actions?: React.ReactNode;
  children: React.ReactNode;
  /** Extra classes on the card wrapper. */
  className?: string;
  /** Extra classes on the content region (defaults to `space-y-4`). */
  contentClassName?: string;
}

/**
 * The standard settings/form card: `rounded-lg border border-border
 * bg-card p-4` with an optional heading row and a `space-y-4` content
 * region. Matches the section shape used by the canonical
 * `EmailBrandingPage`. Compose form primitives (`Input`, `Checkbox`,
 * `Select`, …) inside it rather than hand-rolling card borders/spacing
 * per page.
 */
export function FormSection({
  title,
  description,
  actions,
  children,
  className,
  contentClassName,
}: FormSectionProps) {
  const hasHeader = title || description || actions;
  return (
    <section className={cn('rounded-lg border border-border bg-card p-4', className)}>
      {hasHeader && (
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            {title && <h3 className="text-base font-semibold">{title}</h3>}
            {description && (
              <p className="text-sm text-muted-foreground">{description}</p>
            )}
          </div>
          {actions && (
            <div className="flex shrink-0 items-center gap-2">{actions}</div>
          )}
        </div>
      )}
      <div className={cn('space-y-4', contentClassName)}>{children}</div>
    </section>
  );
}

export default FormSection;
